'use client';
import { useSetState } from 'ahooks';
import Lottie, { type LottieRefCurrentProps } from 'lottie-react';
import { useTranslations } from 'next-intl';
import { useEffect, useMemo, useRef } from 'react';
import projectListTitleEn from '@/app/assets/imgs/project-list-title-en.json';
import projectListTitleZh from '@/app/assets/imgs/project-list-title-zh.json';
import { BaseFillIntelligentDuration } from '@/app/components/icons/BaseFillIntelligentDuration';
import { BaseLineLeft } from '@/app/components/icons/BaseLineLeft';
import { BaseLineUp2 } from '@/app/components/icons/BaseLineUp2';
import { PlanDialogScene } from '@/app/components/shared/PlanDetailDialog';
import { Button } from '@/app/components/ui/button';
import { AGENT_SCENE_SETTINGS_CONFIG, DEFAULT_SCENE_ID, SCENE_INPUT_CONFIG, UPLOAD_SCENE } from '@/app/constants';
import { useGlobalModal, useGoBack } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { AgentChatInput, AgentMediaItem } from '@/app/types';
import { isUltimateOrHigherPlan, reportEvent } from '@/app/utils';
import { openInNewTab } from '@/app/utils/client';
import { useLocale, usePathname, useRouter } from '@/i18n/routing';
import { useGetVoiceLanguage } from '../../_hooks/use-get-voice-language';
import { useCreateNewProject } from '../../_hooks/use-project-store';
import { useUploadManager } from '../../_hooks/use-upload-manager';
import { getDefaultSceneSettings } from '../../_utils';
import { ChatInput } from '../ChatInput';
import { LibraryProjectList } from './components/LibraryProjectList';
import { ProjectList } from './components/ProjectList';
import { agentProjectPageBg, CLAW_ICON_IMAGE_URL } from './constants';

// Lottie 标题动画尺寸（px）
const PROJECT_LIST_TITLE_WIDTH_PX = 655;
const PROJECT_LIST_TITLE_HEIGHT_PX = 96;

export const AgentProject = () => {
  const { userInfo, isUserInfoInitialized, agentInviteInfo, setTempTabInfo, isTeamSpace, clawStatus } = useStore(
    (state) => ({
      isUserInfoInitialized: state.isUserInfoInitialized,
      userInfo: state.userInfo,
      setTempTabInfo: state.setTempTabInfo,
      agentInviteInfo: state.agentInviteInfo,
      isTeamSpace: state.isTeamSpace,
      clawStatus: state.clawStatus,
    }),
  );
  const { open: openPlanDetailDialog } = useGlobalModal('planDetailDialog');
  const { open: openPurchaseCreditsDialog } = useGlobalModal('purchaseCreditsDialog');
  const { open: openConfirmDialog } = useGlobalModal('confirmDialog');
  const { open: openAgentInviteDialog } = useGlobalModal('agentInviteDialog');
  const { open: openLoginDialog } = useGlobalModal('loginDialog');
  const t = useTranslations();
  const locale = useLocale();
  const projectListTitle = locale === 'zh' ? projectListTitleZh : projectListTitleEn;
  const router = useRouter();
  const goBack = useGoBack();
  const lottieRef = useRef<LottieRefCurrentProps | null>(null);
  const { autoAudioItem } = useGetVoiceLanguage();
  const { canCreateProject } = useCreateNewProject();
  const pathname = usePathname();
  const [localState, setLocalState] = useSetState<AgentChatInput>({
    inputValue: [],
    uploadList: [],
    selectedSettings: getDefaultSceneSettings(DEFAULT_SCENE_ID),
    sending: false,
    sceneId: DEFAULT_SCENE_ID,
    selectedVoice: autoAudioItem,
  });

  useEffect(() => {
    if (localState.sceneId !== 'remake') {
      setLocalState((prev) => ({ uploadList: prev.uploadList.filter((item) => item.type !== 'video') }));
    }
  }, [localState.sceneId]);

  // 初始化数据
  useEffect(() => {
    if (lottieRef.current) {
      lottieRef.current.setSpeed(0.5);
    }
  }, []);

  useEffect(() => {
    if (isUserInfoInitialized) {
      if (!userInfo) {
        openLoginDialog();
        reportEvent('login_pop_exposure', {
          refer_element: 'project-create',
          refer_page: pathname,
        });
      }
    }
  }, [isUserInfoInitialized, userInfo]);

  const checkCanCreateProject = async () => {
    const createProjectResult = await canCreateProject();
    if (createProjectResult === 'invited') {
      openAgentInviteDialog();
      reportEvent('agent_friends_invites_view', {
        refer_element: 'project_create',
      });
      return false;
    }
    if (createProjectResult === 'upgrade') {
      openPlanDetailDialog({ scene: PlanDialogScene.AgentProject, fromPage: 'project_create' });
      reportEvent('vip_price_modal_exposure', {
        from_page: 'project_create',
      });
      return false;
    }
    if (createProjectResult === 'purchase') {
      if (isTeamSpace) {
        openConfirmDialog({
          title: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.title'),
          description: t('popup.dialog.error.teamCreditsInsufficientContactAdmin.content'),
          primaryButtonText: t('popup.dialog.error.confirm'),
        });
      } else {
        openPurchaseCreditsDialog({ fromAgentProject: true });
        reportEvent('purchase_credits_exposure', { from_position: 'project_create' });
      }
      reportEvent('credit_modal_exposure', {
        from_page: 'project_create',
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (await checkCanCreateProject()) {
      setLocalState({ sending: true });
      setTempTabInfo(localState);
      router.push('/project/new');
    }
  };

  const inputConfig = useMemo(() => SCENE_INPUT_CONFIG[localState.sceneId || DEFAULT_SCENE_ID], [localState.sceneId]);

  const addUploadFile = (file: AgentMediaItem) => {
    setLocalState((prev) => ({ uploadList: [...prev.uploadList, file] }));
  };

  const updateUploadFile = (id: string, updates: Partial<AgentMediaItem>) => {
    setLocalState((prev) => ({
      uploadList: prev.uploadList.map((item) => (item.tempId === id ? { ...item, ...updates } : item)),
    }));
  };

  const removeUploadFile = (id: string) => {
    setLocalState((prev) => ({ uploadList: prev.uploadList.filter((item) => ![item.id, item.tempId].includes(id)) }));
  };

  const { selectImages, selectVideo, canUpload } = useUploadManager({
    imageLimit: inputConfig?.inputTypesLimit?.image || 0,
    videoLimit: inputConfig?.inputTypesLimit?.video || 0,
    scene: UPLOAD_SCENE.AGENT,
    uploadList: localState.uploadList || [],
    addUploadFile: addUploadFile,
    updateUploadFile: updateUploadFile,
    removeUploadFile: removeUploadFile,
  });

  return (
    <div className="relative flex h-screen w-full flex-col items-center justify-center gap-6 bg-[#141416] bg-bottom bg-no-repeat">
      <div className="absolute inset-0 flex items-start justify-center">
        <img src={agentProjectPageBg} alt="agent-project-list-bg" className="w-full object-cover" />
      </div>

      <Button
        variant="outline"
        className="absolute top-3 left-3 z-[11] flex h-8 items-center gap-1 bg-system-white04 px-2"
        onClick={() => {
          goBack();
        }}
      >
        <BaseLineLeft className="size-4" />
        {t('agentProject.back')}
      </Button>

      {!isTeamSpace && (isUltimateOrHigherPlan(userInfo?.subs_plan) || agentInviteInfo?.status === 'not_invited') && (
        <div
          className="absolute top-3 right-3 z-[11] flex cursor-pointer items-center gap-2 rounded-8 border-1 border-system-white08 bg-[rgba(77,109,255,0.24)] px-2.5 py-1.5"
          onClick={() => {
            openAgentInviteDialog();
            reportEvent('agent_friends_invites_view', {
              refer_element: 'friendsinvites_button',
            });
          }}
        >
          <BaseFillIntelligentDuration className="size-4" variant="blueToWhite" />
          <div className="text-[#8A9FFF] text-sm">
            {isUltimateOrHigherPlan(userInfo?.subs_plan)
              ? t('agentProject.invite.ultimateShrinkTitle')
              : t('agentProject.invite.shrinkTitle')}
          </div>
        </div>
      )}

      <div
        id="projectScrollableDiv"
        className="z-10 flex h-full w-full flex-col items-center gap-6 overflow-y-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <div className="mt-[6%] flex flex-col items-center justify-center gap-2">
          {/* 标题 */}
          <div className="flex items-center justify-center">
            <Lottie
              lottieRef={lottieRef}
              animationData={projectListTitle}
              loop={false}
              autoplay={true}
              style={{
                width: PROJECT_LIST_TITLE_WIDTH_PX,
                height: PROJECT_LIST_TITLE_HEIGHT_PX,
              }}
              className="text-white"
            />
          </div>
          {/* 副标题 */}
          <div className="text-base text-system-white64">{t('agentProject.subtitle')}</div>
        </div>

        <div className="w-full max-w-4xl">
          {userInfo?.subs_plan !== 'team_v2' && !isTeamSpace && (
            <div
              className="mx-6 flex cursor-pointer items-center justify-between rounded-t-12 border-[rgba(255,255,255,0.08)] border-x-1 border-t-1 bg-[linear-gradient(90deg,rgba(179,138,255,0.2)_0%,rgba(142,77,255,0.2)_100%)] px-3 py-2"
              onClick={() => {
                if (clawStatus === 'CLAW_STATUS_ACTIVE') {
                  openInNewTab(`${window.location.origin}/claw`);
                } else {
                  router.push('/vidu-claw');
                }
              }}
            >
              <div className="relative flex items-center gap-2 pl-8">
                <img
                  src={CLAW_ICON_IMAGE_URL}
                  alt="claw-icon"
                  className="-top-5 -left-4 absolute size-16 rounded-full object-cover"
                />
                <div className="h-[18px] bg-[linear-gradient(90deg,#FFFFFF_0%,#DC8AFF_100%)] bg-clip-text text-[13px] text-transparent leading-[18px]">
                  {t('agentProject.clawOnline')}
                </div>
              </div>
              <BaseLineUp2 className="size-4 rotate-90 text-[#DC8AFF]" />
            </div>
          )}

          {/* 输入框区域 */}
          <ChatInput
            className="w-full rounded-3xl bg-[#17181b] bg-none py-1"
            onSend={handleSubmit}
            settings={localState.selectedSettings}
            settingsConfig={AGENT_SCENE_SETTINGS_CONFIG.common}
            onUpdateSetting={(key, value) =>
              setLocalState((prev) => ({ selectedSettings: { ...prev.selectedSettings, [key]: value } }))
            }
            inputValue={localState.inputValue}
            uploadList={localState.uploadList}
            sending={localState.sending}
            onInputChange={(value) => setLocalState({ inputValue: value })}
            onRemoveFile={(id) =>
              setLocalState((prev) => ({
                uploadList: prev.uploadList.filter((item) => ![item.id, item.tempId].includes(id)),
              }))
            }
            selectImages={selectImages}
            selectVideo={selectVideo}
            canUpload={canUpload}
            sceneId={localState.sceneId}
            onUpdateSceneId={(sceneId) => setLocalState({ sceneId })}
            selectedVoice={localState.selectedVoice}
            onUpdateSelectedVoice={(selectedVoice) => setLocalState({ selectedVoice })}
            onAddModel={(model) =>
              setLocalState((prev) => ({
                inputValue: [
                  ...prev.inputValue,
                  {
                    type: 'agent-model',
                    agentModel: {
                      id: model.id,
                      name: model.name,
                      description: model.description,
                      originalDescription: model.description,
                      avatar: model.img,
                    },
                  },
                ],
              }))
            }
          />
        </div>

        <ProjectList checkCanCreateProject={checkCanCreateProject} />

        <LibraryProjectList />
      </div>
    </div>
  );
};
